import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { io as socketIOClient } from 'socket.io-client';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './CapturaPage.css';
import { baseColumnDefs, parseFechaPedido } from './baseDatosColumns';
import { API_BASE_URL, SOCKET_URL } from './config';
import { getUserRoles } from './userRoles';

ModuleRegistry.registerModules([AllCommunityModule]);

const CAPTURA_EDITABLE_FIELDS = new Set(['CODIGO', 'CHOFER', 'PEDIDO', 'OC']);
const COLUMN_VISIBILITY_STORAGE_KEY = 'capturaColumnVisibility';
const CAPTURA_HISTORY_LIMIT = 3;

const IMPRESION_ALDO_DB = 'ravCapturaImpresion';
const IMPRESION_ALDO_STORE = 'config';
const IMPRESION_ALDO_HANDLE_KEY = 'carpetaImpresionAldo';

const openImpresionAldoDb = () => new Promise((resolve, reject) => {
  const request = window.indexedDB.open(IMPRESION_ALDO_DB, 1);

  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(IMPRESION_ALDO_STORE)) {
      db.createObjectStore(IMPRESION_ALDO_STORE);
    }
  };

  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const saveImpresionAldoHandle = async (handle) => {
  const db = await openImpresionAldoDb();

  await new Promise((resolve, reject) => {
    const tx = db.transaction(IMPRESION_ALDO_STORE, 'readwrite');
    tx.objectStore(IMPRESION_ALDO_STORE).put(
      handle,
      IMPRESION_ALDO_HANDLE_KEY
    );

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

  db.close();
};

const loadImpresionAldoHandle = async () => {
  const db = await openImpresionAldoDb();

  const handle = await new Promise((resolve, reject) => {
    const tx = db.transaction(IMPRESION_ALDO_STORE, 'readonly');
    const request = tx.objectStore(IMPRESION_ALDO_STORE).get(
      IMPRESION_ALDO_HANDLE_KEY
    );

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });

  db.close();
  return handle;
};

const hasImpresionAldoPermission = async (handle, requestPermission = false) => {
  if (!handle) return false;

  const options = { mode: 'readwrite' };

  if (typeof handle.queryPermission === 'function') {
    const current = await handle.queryPermission(options);
    if (current === 'granted') return true;
  }

  if (requestPermission && typeof handle.requestPermission === 'function') {
    const requested = await handle.requestPermission(options);
    return requested === 'granted';
  }

  return false;
};

const normalizarTaller = (valor) => {
  if (!valor) return '';
  return valor.toString().trim().toLowerCase().replace(/\s+/g, ' ');
};

const normalizarAseguradora = (valor) => {
  if (!valor) return '';
  return valor.toString().trim().toUpperCase().replace(/\s+/g, ' ');
};


const normalizarTextoBusqueda = (valor) => (
  (valor ?? '')
    .toString()
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const esQualitasXnuupAseguradora = (aseguradora) => {
  const normalized = normalizarTextoBusqueda(aseguradora);
  return normalized.includes('QUALITAS') || normalized.includes('XNUUP') || normalized.includes('XNUUB');
};

const esCodigoQualitas = (codigo) => {
  const normalized = normalizarTextoBusqueda(codigo);
  return normalized.startsWith('QUA') || normalized.startsWith('QUF');
};



const STOPWORDS_TALLER_QUALITAS = new Set([
  'DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y', 'SA', 'CV', 'SAS',
  'TALLER', 'TALLERES', 'SERVICIO', 'SERVICIOS',
  'AUTOMOTRIZ', 'AUTOMOTRICES', 'CENTRO', 'COLISION', 'COLISIONES',
  'MULTIMARCA'
]);

const tokensTallerQualitas = (valor) => (
  normalizarTextoBusqueda(valor)
    .split(' ')
    .filter(token => token.length >= 3 && !STOPWORDS_TALLER_QUALITAS.has(token))
);

const buscarLocalidadQualitasFlexible = (opciones, tallerBuscado) => {
  const textoBuscado = normalizarTextoBusqueda(tallerBuscado);
  const tokensBuscado = tokensTallerQualitas(tallerBuscado);

  if (!textoBuscado || tokensBuscado.length === 0 || !Array.isArray(opciones)) {
    return { codigo: '', nomenclatura: '' };
  }

  let mejor = null;
  let mejorPuntaje = 0;

  opciones.forEach((opcion) => {
    if (!esCodigoQualitas(opcion.codigo)) return;

    const textoCatalogo = normalizarTextoBusqueda(opcion.taller);
    const tokensCatalogo = tokensTallerQualitas(opcion.taller);

    if (!textoCatalogo || tokensCatalogo.length === 0) return;

    let puntaje = 0;

    if (textoBuscado === textoCatalogo) puntaje += 100;
    if (textoBuscado.includes(textoCatalogo) || textoCatalogo.includes(textoBuscado)) puntaje += 30;

    const setCatalogo = new Set(tokensCatalogo);
    const coincidencias = tokensBuscado.filter(token => setCatalogo.has(token)).length;
    puntaje += coincidencias * 10;

    if (puntaje > mejorPuntaje) {
      mejorPuntaje = puntaje;
      mejor = opcion;
    }
  });

  return mejor && mejorPuntaje >= 20
    ? mejor
    : { codigo: '', nomenclatura: '' };
};


const resolveNomenclaturaPreferida = (aseguradora) => {
  const normalized = normalizarAseguradora(aseguradora);
  if (!normalized) return '';
  if (normalized.includes('MULTIASISTENCIA')) return 'TML';
  if (normalized.includes('MAPFRE TEPEYAC')) return 'MAP';
  if (normalized.includes('CHUBB SEGUROS MEXICO')) return 'INPCH';
  return '';
};

const findLocalidadInfoForAseguradora = (opcionesLocalidad, aseguradora) => {
  if (!Array.isArray(opcionesLocalidad) || opcionesLocalidad.length === 0) {
    return { codigo: '', nomenclatura: '' };
  }

  if (esQualitasXnuupAseguradora(aseguradora)) {
    const matchQualitas = opcionesLocalidad.find((opcion) => esCodigoQualitas(opcion.codigo));
    if (matchQualitas) {
      return matchQualitas;
    }
    return { codigo: '', nomenclatura: '' };
  }

  const nomenclaturaPreferida = resolveNomenclaturaPreferida(aseguradora);
  if (nomenclaturaPreferida) {
    const match = opcionesLocalidad.find((opcion) => (
      (opcion.nomenclatura || '').toString().trim().toUpperCase() === nomenclaturaPreferida
    ));
    if (match) {
      return match;
    }
    return { codigo: '', nomenclatura: '' };
  }

  return opcionesLocalidad[opcionesLocalidad.length - 1] || { codigo: '', nomenclatura: '' };
};

const normalizeKeyPart = (value) => (value ?? '').toString().trim().toUpperCase();

const buildSiniestroItemKey = (siniestro, item) => {
  const siniestroKey = normalizeKeyPart(siniestro);
  const itemKey = normalizeKeyPart(item);
  if (!siniestroKey && !itemKey) return '';
  return `${siniestroKey}|||${itemKey}`;
};

const extractSiniestro = (row) => row?.SINIESTRO ?? row?.siniestro ?? row?.Siniestro ?? '';
const extractItem = (row) => row?.ITEM ?? row?.item ?? row?.Item ?? '';
const parseTimestampHistory = (history) => {
  if (!Array.isArray(history)) return [];
  return history
    .map((value) => {
      if (!value) return null;
      const date = value instanceof Date ? value : new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    })
    .filter(Boolean)
    .slice(0, CAPTURA_HISTORY_LIMIT);
};

const formatTimestamp = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};
const buildCapturaRowIdentity = (row) => {
  if (!row || typeof row !== 'object') return '';
  if (row.id !== undefined && row.id !== null) {
    return `id:${row.id}`;
  }
  const pedidoRaw = row.PEDIDO ?? row.pedido ?? row.Pedido ?? '';
  const pedido = pedidoRaw == null ? '' : String(pedidoRaw).trim();
  const siniestroItemKey = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
  if (pedido || siniestroItemKey) {
    return `pedido:${pedido}::${siniestroItemKey}`;
  }
  return JSON.stringify(row);
};

const parseCostoToNumber = (raw) => {
  if (raw === null || raw === undefined || raw === '') return NaN;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const cleaned = String(raw).replace(/[^0-9.-]/g, '').trim();
  if (cleaned === '') return NaN;
  const containsLetters = /[a-zA-Z]/.test(String(raw));
  if (containsLetters) return NaN;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const isCodigoRadec = (codigo) => (
  /^\d{3}-\d{4}-\d{2}$/.test((codigo ?? '').toString().trim())
);

const resolveCodigoPorCosto = (row) => {
  const numeric = parseCostoToNumber(row.COSTO);

  if (Number.isFinite(numeric) && numeric > 1 && isCodigoRadec(row?.CODIGO)) {
    return 'GDL001';
  }

  if (Number.isFinite(numeric) && numeric > 1) {
    return 'GDL002';
  }

  return 'GDL005';
};

const buildFolioConSufijo = (pedido, esAjustado) => {
  if (!pedido) return '';
  if (esAjustado) {
    return `${pedido}031`;
  }
  return `${pedido}03`;
};


const buildFolioQualitasDesdeSiniestro = (siniestro) => {
  const valor = (siniestro ?? '').toString().trim();
  if (!valor) return '';
  return valor.replace(/\D/g, '');
};


const resolveFolioPorAseguradora = (row, folioDefault = '') => {
  const aseguradora = normalizarAseguradora(row?.ASEGURADORA ?? row?.aseguradora ?? '');

  if (esQualitasXnuupAseguradora(aseguradora)) {
    const siniestro = (row?.SINIESTRO ?? row?.siniestro ?? '').toString().trim();
    const folioQualitas = buildFolioQualitasDesdeSiniestro(siniestro);
    if (folioQualitas) return folioQualitas;
  }


  return (folioDefault ?? '').toString().trim();
};

const expandRowByCodigo = (row, valueField = 'COSTO') => {
  const codigoRaw = row.CODIGO;
  const costoRaw = row[valueField];
  const splitCodigo = typeof codigoRaw === 'string' && codigoRaw.includes('/');
  const splitCosto = typeof costoRaw === 'string' && costoRaw.includes('/');
  if (!splitCodigo && !splitCosto) {
    return [{ ...row }];
  }
  const codigoParts = splitCodigo
    ? codigoRaw.split('/').map(part => part.trim()).filter(part => part !== '')
    : [codigoRaw];
  const costoParts = splitCosto
    ? costoRaw.split('/').map(part => part.trim()).filter(part => part !== '')
    : [costoRaw];
  const fallbackCodigo = codigoParts.length ? codigoParts[codigoParts.length - 1] : '';
  const fallbackCosto = costoParts.length ? costoParts[costoParts.length - 1] : '';
  const iterations = Math.max(codigoParts.length, costoParts.length, 1);
  return Array.from({ length: iterations }).map((_, idx) => ({
    ...row,
    CODIGO: codigoParts[idx] !== undefined ? codigoParts[idx] : fallbackCodigo,
    [valueField]: costoParts[idx] !== undefined ? costoParts[idx] : fallbackCosto,
  }));
};

const resolveCostoConDefault = (row) => {
  const numeric = parseCostoToNumber(row.COSTO);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return 1;
};

const resolvePrecioConDefault = (row) => {
  const numeric = parseCostoToNumber(row.PRECIO);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return 1;
};


const COMPAQ_BASE_TEMPLATE_URL = '/compaq/PLANTILLA_COMPAQ_BASE.xlsx';
const COMPAQ_SHEET_XML = 'xl/worksheets/sheet1.xml';
const COMPAQ_TABLE_XML = 'xl/tables/table1.xml';
const COMPAQ_SHARED_STRINGS_XML = 'xl/sharedStrings.xml';
const COMPAQ_XML_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const COMPAQ_XML_SPACE_NS = 'http://www.w3.org/XML/1998/namespace';
const COMPAQ_COLUMN_LETTERS = 'ABCDEFGHIJKLMNOPQR'.split('');

const fechaCompaqAExcelSerial = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return null;

  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;

  const dia = Number(match[1]);
  const mes = Number(match[2]);
  const anio = Number(match[3]);

  const utc = Date.UTC(anio, mes - 1, dia);
  const fecha = new Date(utc);

  if (
    fecha.getUTCFullYear() !== anio
    || fecha.getUTCMonth() !== mes - 1
    || fecha.getUTCDate() !== dia
  ) {
    return null;
  }

  return (utc - Date.UTC(1899, 11, 30)) / 86400000;
};

const parseNumeroCompaq = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const text = String(value ?? '')
    .replace(/[$,\s]/g, '')
    .trim();

  if (!text || !/^-?\d+(?:\.\d+)?$/.test(text)) return null;

  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : null;
};

const generarXlsxDesdeMoldeCompaq = async (data) => {
  const response = await fetch(COMPAQ_BASE_TEMPLATE_URL, { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(
      `No se pudo cargar el molde COMPAC (${response.status}).`
    );
  }

  const templateBuffer = await response.arrayBuffer();
  const zip = await JSZip.loadAsync(templateBuffer);

  const sheetFile = zip.file(COMPAQ_SHEET_XML);
  const tableFile = zip.file(COMPAQ_TABLE_XML);
  const sharedStringsFile = zip.file(COMPAQ_SHARED_STRINGS_XML);

  if (!sheetFile || !tableFile || !sharedStringsFile) {
    throw new Error(
      'El molde COMPAC no contiene sheet1.xml, table1.xml o sharedStrings.xml.'
    );
  }

  const [sheetXml, tableXml, sharedStringsXml] = await Promise.all([
    sheetFile.async('string'),
    tableFile.async('string'),
    sharedStringsFile.async('string')
  ]);

  const parser = new DOMParser();
  const serializer = new XMLSerializer();

  const sheetDoc = parser.parseFromString(sheetXml, 'application/xml');
  const tableDoc = parser.parseFromString(tableXml, 'application/xml');
  const sharedStringsDoc = parser.parseFromString(
    sharedStringsXml,
    'application/xml'
  );

  if (
    sheetDoc.getElementsByTagName('parsererror').length
    || tableDoc.getElementsByTagName('parsererror').length
    || sharedStringsDoc.getElementsByTagName('parsererror').length
  ) {
    throw new Error('No se pudo interpretar el XML del molde COMPAC.');
  }

  const sheetData = sheetDoc.getElementsByTagNameNS(
    COMPAQ_XML_NS,
    'sheetData'
  )[0];

  if (!sheetData) {
    throw new Error('El molde COMPAC no contiene sheetData.');
  }

  const originalRows = Array.from(
    sheetData.getElementsByTagNameNS(COMPAQ_XML_NS, 'row')
  );

  const headerTemplate = originalRows.find(
    (row) => row.getAttribute('r') === '1'
  );
  const oddTemplate = originalRows.find(
    (row) => row.getAttribute('r') === '2'
  );
  const evenTemplate = originalRows.find(
    (row) => row.getAttribute('r') === '3'
  ) || oddTemplate;

  if (!headerTemplate || !oddTemplate || !evenTemplate) {
    throw new Error(
      'El molde COMPAC no contiene las filas modelo 1, 2 y 3.'
    );
  }

  const headerRow = headerTemplate.cloneNode(true);
  const oddRowTemplate = oddTemplate.cloneNode(true);
  const evenRowTemplate = evenTemplate.cloneNode(true);

  while (sheetData.firstChild) {
    sheetData.removeChild(sheetData.firstChild);
  }

  sheetData.appendChild(headerRow);

  const sharedStringsRoot = sharedStringsDoc.documentElement;
  const sharedStringItems = Array.from(
    sharedStringsDoc.getElementsByTagNameNS(COMPAQ_XML_NS, 'si')
  );

  const sharedStringIndex = new Map();

  sharedStringItems.forEach((si, index) => {
    const text = Array.from(
      si.getElementsByTagNameNS(COMPAQ_XML_NS, 't')
    )
      .map((node) => node.textContent || '')
      .join('');

    if (!sharedStringIndex.has(text)) {
      sharedStringIndex.set(text, index);
    }
  });

  const getSharedStringIndex = (value) => {
    const text = String(value ?? '');

    if (sharedStringIndex.has(text)) {
      return sharedStringIndex.get(text);
    }

    const si = sharedStringsDoc.createElementNS(COMPAQ_XML_NS, 'si');
    const t = sharedStringsDoc.createElementNS(COMPAQ_XML_NS, 't');

    if (/^\s|\s$/.test(text)) {
      t.setAttributeNS(
        COMPAQ_XML_SPACE_NS,
        'xml:space',
        'preserve'
      );
    }

    t.textContent = text;
    si.appendChild(t);
    sharedStringsRoot.appendChild(si);

    const index = sharedStringsDoc.getElementsByTagNameNS(
      COMPAQ_XML_NS,
      'si'
    ).length - 1;

    sharedStringIndex.set(text, index);
    return index;
  };

  const setBlankCell = (cell) => {
    while (cell.firstChild) {
      cell.removeChild(cell.firstChild);
    }
    cell.removeAttribute('t');
  };

  const setNumericCell = (cell, value) => {
    setBlankCell(cell);

    if (value === null || typeof value === 'undefined' || value === '') {
      return;
    }

    const v = sheetDoc.createElementNS(COMPAQ_XML_NS, 'v');
    v.textContent = String(value);
    cell.appendChild(v);
  };

  const setTextCell = (cell, value) => {
    setBlankCell(cell);

    const text = value == null ? '' : String(value);
    if (text === '') return;

    cell.setAttribute('t', 's');

    const v = sheetDoc.createElementNS(COMPAQ_XML_NS, 'v');
    v.textContent = String(getSharedStringIndex(text));
    cell.appendChild(v);
  };

  data.forEach((sourceRow, dataIndex) => {
    const rowNumber = dataIndex + 2;

    const rowNode = (
      dataIndex % 2 === 0
        ? oddRowTemplate
        : evenRowTemplate
    ).cloneNode(true);

    rowNode.setAttribute('r', String(rowNumber));

    const cells = Array.from(
      rowNode.getElementsByTagNameNS(COMPAQ_XML_NS, 'c')
    );

    if (cells.length < 18) {
      throw new Error(
        `La fila modelo COMPAC solo contiene ${cells.length} celdas; se esperaban 18.`
      );
    }

    const values = Array.isArray(sourceRow)
      ? sourceRow.slice(0, 18)
      : [];

    COMPAQ_COLUMN_LETTERS.forEach((columnLetter, columnIndex) => {
      const cell = cells[columnIndex];
      const value = values[columnIndex] ?? '';

      cell.setAttribute('r', `${columnLetter}${rowNumber}`);

      // E = Fecha real de Excel.
      if (columnIndex === 4) {
        const serial = fechaCompaqAExcelSerial(value);
        if (serial === null) {
          setBlankCell(cell);
        } else {
          setNumericCell(cell, serial);
        }
        return;
      }

      // C = Folio, P = Cantidad, Q = Precio, R = Total.
      // Se escriben numéricos cuando el valor realmente es numérico.
      if ([2, 15, 16, 17].includes(columnIndex)) {
        const numeric = parseNumeroCompaq(value);
        if (numeric !== null) {
          setNumericCell(cell, numeric);
        } else {
          setTextCell(cell, value);
        }
        return;
      }

      setTextCell(cell, value);
    });

    sheetData.appendChild(rowNode);
  });

  const lastRow = Math.max(1, data.length + 1);
  const tableRange = `A1:R${lastRow}`;

  const dimension = sheetDoc.getElementsByTagNameNS(
    COMPAQ_XML_NS,
    'dimension'
  )[0];

  if (dimension) {
    dimension.setAttribute('ref', tableRange);
  }

  tableDoc.documentElement.setAttribute('ref', tableRange);

  Array.from(
    tableDoc.getElementsByTagNameNS(COMPAQ_XML_NS, 'autoFilter')
  ).forEach((node) => {
    node.setAttribute('ref', tableRange);
  });

  Array.from(
    tableDoc.getElementsByTagNameNS(COMPAQ_XML_NS, 'sortState')
  ).forEach((node) => {
    node.setAttribute('ref', tableRange);
  });

  const countSharedStringCells = (doc) => (
    Array.from(
      doc.getElementsByTagNameNS(COMPAQ_XML_NS, 'c')
    ).filter((cell) => cell.getAttribute('t') === 's').length
  );

  let sharedStringReferenceCount = countSharedStringCells(sheetDoc);

  const otherWorksheetNames = Object.keys(zip.files).filter(
    (name) => (
      /^xl\/worksheets\/sheet\d+\.xml$/.test(name)
      && name !== COMPAQ_SHEET_XML
    )
  );

  for (const worksheetName of otherWorksheetNames) {
    const worksheetFile = zip.file(worksheetName);
    if (!worksheetFile) continue;

    const worksheetXml = await worksheetFile.async('string');
    const worksheetDoc = parser.parseFromString(
      worksheetXml,
      'application/xml'
    );

    if (!worksheetDoc.getElementsByTagName('parsererror').length) {
      sharedStringReferenceCount += countSharedStringCells(worksheetDoc);
    }
  }

  sharedStringsRoot.setAttribute(
    'count',
    String(sharedStringReferenceCount)
  );

  sharedStringsRoot.setAttribute(
    'uniqueCount',
    String(
      sharedStringsDoc.getElementsByTagNameNS(
        COMPAQ_XML_NS,
        'si'
      ).length
    )
  );

  zip.file(
    COMPAQ_SHEET_XML,
    serializer.serializeToString(sheetDoc)
  );

  zip.file(
    COMPAQ_TABLE_XML,
    serializer.serializeToString(tableDoc)
  );

  zip.file(
    COMPAQ_SHARED_STRINGS_XML,
    serializer.serializeToString(sharedStringsDoc)
  );

  return zip.generateAsync({
    type: 'arraybuffer',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });
};

const pedidoTemplateColumns = [
  { header: 'Generado', valueGetter: () => '' },
  { header: 'Bitacora', valueGetter: () => '' },
  {
    header: 'Folio',
    valueGetter: (row) => resolveFolioPorAseguradora(row, row.PEDIDO),
  },
  {
    header: 'Serie',
    valueGetter: (row) => (row.__serieNomenclatura || ''),
  },
  {
    header: 'Fecha',
    valueGetter: (row) => {
      const raw = row.FECHA_PEDIDO;
      if (!raw) return '';
      const parsed = parseFechaPedido(raw);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
  {
    header: 'CteProv',
    valueGetter: (row) => (row.__cteProv || ''),
  },
  { header: 'RazonSocial', valueGetter: () => '' },
  { header: 'Agente', valueGetter: () => '' },
  {
    header: 'Referencia',
    valueGetter: (row) => {
      const aseguradora = (row.ASEGURADORA || '').trim().toUpperCase();
      const siniestro = (row.SINIESTRO || '').toString().trim();
      if (!siniestro) return '';
      if (esQualitasXnuupAseguradora(aseguradora)) {
        return siniestro;
      }
      if (aseguradora.includes('MULTIASISTENCIA')) {
        return siniestro;
      }
      if (aseguradora === 'CHUBB SEGUROS MEXICO / CHUBB SEGUROS MEXICO') {
        return siniestro.slice(0, 11);
      }
      if (aseguradora === 'MAPFRE TEPEYAC, S.A. / MAPFRE TEPEYAC, S.A.') {
        return siniestro;
      }
      return '';
    },
  },
  {
    header: 'Carro',
    valueGetter: (row) => {
      const modelo = (row.MODELO || '').toString().trim();
      const anio = row.ANIO !== undefined && row.ANIO !== null ? String(row.ANIO).trim() : '';
      if (modelo && anio) return `${modelo} ${anio}`;
      if (modelo) return modelo;
      if (anio) return anio;
      return '';
    },
  },
  {
    header: 'Taller2',
    valueGetter: (row) => (row.NOMBRE_COMERCIAL_TALLER || '').toString().trim(),
  },
  {
    header: 'Orden',
    valueGetter: (row) => (row.OC || '').toString().trim(),
  },
  {
    header: 'Codigo3',
    valueGetter: (row) => (row.CODIGO || '').toString().trim(),
  },
  {
    header: 'NombreProducto',
    valueGetter: (row) => (row.ITEM || '').toString().trim(),
  },
  { header: 'Almacen', valueGetter: () => '999' },
  { header: 'Cantidad', valueGetter: () => '1' },
  {
    header: 'Precio',
    valueGetter: (row) => resolvePrecioConDefault(row),
    format: 'currency',
  },
  {
    header: 'Columna1',
    valueGetter: (row) => resolvePrecioConDefault(row),
    format: 'currency',
  },
  { header: '', valueGetter: () => '' },
];

const compraTemplateColumns = [
  { header: 'Generado', valueGetter: () => '' },
  { header: 'Bitacora', valueGetter: () => '' },
  {
    header: 'Folio',
    valueGetter: (row) => {
      const pedido = row.PEDIDO ?? '';
      const aseguradora = normalizarAseguradora(row?.ASEGURADORA ?? row?.aseguradora ?? '');
      if (esQualitasXnuupAseguradora(aseguradora)) {
        const siniestro = (row?.SINIESTRO ?? row?.siniestro ?? '').toString().trim();
        const folioQualitas = buildFolioQualitasDesdeSiniestro(siniestro);
        if (!folioQualitas) return '';
        return row.__folioAjustadoFlag ? `${folioQualitas}1` : folioQualitas;
      }
      if (aseguradora.includes('MULTIASISTENCIA')) {
        const siniestro = (row?.SINIESTRO ?? row?.siniestro ?? '').toString().trim();
        if (!siniestro) return '';
        const folioMulti = siniestro.slice(1);
        return row.__folioAjustadoFlag ? `${folioMulti}1` : folioMulti;
      }
      if (row.__folioAjustado) {
        return resolveFolioPorAseguradora(row, row.__folioAjustado);
      }
      return resolveFolioPorAseguradora(row, buildFolioConSufijo(pedido, false));
    },
  },
  { header: 'Serie', valueGetter: () => 'RAV' },
  // { header: 'Fecha', valueGetter: () => '' }, // Eliminada
  {
    header: 'Fecha 2',
    valueGetter: (row) => {
      const raw = row.FECHA_PEDIDO;
      if (!raw) return '';
      const parsed = parseFechaPedido(raw);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
  {
    header: 'Codigo',
    valueGetter: (row) => resolveCodigoPorCosto(row),
  },
  { header: 'Taller', valueGetter: () => '' },
  { header: 'Agente', valueGetter: (row) => (row.__agenteCompra || '') },
  {
    header: 'Referencia',
    valueGetter: (row) => {
      const aseguradora = (row.ASEGURADORA || '').trim().toUpperCase();
      const siniestro = (row.SINIESTRO || '').trim();
      if (!siniestro) return '';
      if (aseguradora === 'CHUBB SEGUROS MEXICO / CHUBB SEGUROS MEXICO') {
        return siniestro.slice(0, 11);
      }
      return siniestro;
    }
  },
  {
    header: 'Carro',
    valueGetter: (row) => {
      const modelo = (row.MODELO || '').toString().trim();
      const anio = row.ANIO !== undefined && row.ANIO !== null ? String(row.ANIO).trim() : '';
      if (modelo && anio) return `${modelo} ${anio}`;
      if (modelo) return modelo;
      if (anio) return anio;
      return '';
    }
  },
  {
    header: 'Taller2',
    valueGetter: (row) => (row.NOMBRE_COMERCIAL_TALLER || '').toString().trim(),
  },
  {
    header: 'Orden',
    valueGetter: (row) => (row.OC || '').toString().trim(),
  },
  {
    header: 'Codigo3',
    valueGetter: (row) => (row.CODIGO || '').toString().trim(),
  },
  {
    header: 'NombreProducto',
    valueGetter: (row) => (row.ITEM || '').toString().trim(),
  },
  {
    header: 'Almacen',
    valueGetter: () => '999',
  },
  {
    header: 'Cantidad',
    valueGetter: () => '1',
  },
  {
    header: 'Precio',
    valueGetter: (row) => resolveCostoConDefault(row),
    format: 'currency',
  },
  {
    header: 'Columna1',
    valueGetter: (row) => resolveCostoConDefault(row),
    format: 'currency',
  },
  { header: '', valueGetter: () => '' },
];

const parseMontoPdf = (valor) => {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : 0;
  }

  const limpio = String(valor ?? '')
    .replace(/[$,\s]/g, '')
    .trim();

  const numero = Number(limpio);
  return Number.isFinite(numero) ? numero : 0;
};

const redondearMonedaPdf = (valor) => (
  Math.round((Number(valor) + Number.EPSILON) * 100) / 100
);

const formatearMonedaPdf = (valor) => (
  new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(redondearMonedaPdf(valor))
);

const normalizarNombreArchivoPdf = (valor) => (
  String(valor ?? '')
    .trim()
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 100)
);

const numeroEnteroALetras = (numero) => {
  const n = Math.floor(Math.abs(Number(numero) || 0));

  if (n === 0) return 'CERO';

  const unidades = [
    '', 'UNO', 'DOS', 'TRES', 'CUATRO',
    'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'
  ];

  const especiales = {
    10: 'DIEZ',
    11: 'ONCE',
    12: 'DOCE',
    13: 'TRECE',
    14: 'CATORCE',
    15: 'QUINCE',
    16: 'DIECISEIS',
    17: 'DIECISIETE',
    18: 'DIECIOCHO',
    19: 'DIECINUEVE',
    20: 'VEINTE',
    21: 'VEINTIUNO',
    22: 'VEINTIDOS',
    23: 'VEINTITRES',
    24: 'VEINTICUATRO',
    25: 'VEINTICINCO',
    26: 'VEINTISEIS',
    27: 'VEINTISIETE',
    28: 'VEINTIOCHO',
    29: 'VEINTINUEVE'
  };

  const decenas = [
    '', '', '', 'TREINTA', 'CUARENTA',
    'CINCUENTA', 'SESENTA', 'SETENTA',
    'OCHENTA', 'NOVENTA'
  ];

  const centenas = [
    '', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS',
    'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS',
    'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'
  ];

  const menorMil = (valor) => {
    if (valor === 0) return '';
    if (valor < 10) return unidades[valor];
    if (valor <= 29) return especiales[valor];

    if (valor < 100) {
      const d = Math.floor(valor / 10);
      const u = valor % 10;
      return u ? `${decenas[d]} Y ${unidades[u]}` : decenas[d];
    }

    if (valor === 100) return 'CIEN';

    const c = Math.floor(valor / 100);
    const resto = valor % 100;

    return resto
      ? `${centenas[c]} ${menorMil(resto)}`
      : centenas[c];
  };

  const convertir = (valor) => {
    if (valor < 1000) return menorMil(valor);

    if (valor < 1000000) {
      const miles = Math.floor(valor / 1000);
      const resto = valor % 1000;

      const textoMiles = miles === 1
        ? 'MIL'
        : `${convertir(miles).replace(/UNO$/, 'UN')} MIL`;

      return resto
        ? `${textoMiles} ${convertir(resto)}`
        : textoMiles;
    }

    if (valor < 1000000000000) {
      const millones = Math.floor(valor / 1000000);
      const resto = valor % 1000000;

      const textoMillones = millones === 1
        ? 'UN MILLON'
        : `${convertir(millones).replace(/UNO$/, 'UN')} MILLONES`;

      return resto
        ? `${textoMillones} ${convertir(resto)}`
        : textoMillones;
    }

    return String(valor);
  };

  return convertir(n);
};

const importeALetrasPdf = (importe) => {
  const centavosTotales = Math.round(
    redondearMonedaPdf(importe) * 100
  );

  const enteros = Math.floor(centavosTotales / 100);
  const centavos = centavosTotales % 100;

  const moneda = enteros === 1 ? 'PESO' : 'PESOS';

  return `SON: ${numeroEnteroALetras(enteros)} ${moneda} ${String(centavos).padStart(2, '0')}/100 M.N.`;
};

function CapturaPage() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdatedHistory, setLastUpdatedHistory] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [marking, setMarking] = useState(false);
  const [localidades, setLocalidades] = useState([]);
  const [baseDuplicateKeys, setBaseDuplicateKeys] = useState([]);
  const [showColumnList, setShowColumnList] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnVisibilityLoaded, setColumnVisibilityLoaded] = useState(false);
  const [primarySearchText, setPrimarySearchText] = useState('');
  const [secondarySearchText, setSecondarySearchText] = useState('');
  const [showCompaqActions, setShowCompaqActions] = useState(false);
  const [impresionAldoDirHandle, setImpresionAldoDirHandle] = useState(null);
  const [impresionAldoDirStatus, setImpresionAldoDirStatus] = useState('No configurada');
  const corridaImpresionRef = useRef(null);
  const gridRef = useRef(null);
  const previousRowIdsRef = useRef(new Set());

  const puedeVer = true;
const usuario = useMemo(() => {    try { return JSON.parse(localStorage.getItem('usuario') || '{}'); } catch (_) { return {}; }  }, []);  const userRoles = useMemo(() => getUserRoles(usuario), [usuario]);  const puedeEditarPrecioMultiasistencia = ['supervisor','supervisores','facturacion','cancelacion','cancelaciones','captura'].some((role) => userRoles.includes(role));  const usuarioNombre = useMemo(() => {    const raw = usuario?.username || usuario?.usuario || usuario?.nombreUsuario || usuario?.nombre || '';    return raw ? String(raw).trim() : '';  }, [usuario]);

  useEffect(() => {
    let cancelled = false;

    const cargarCarpetaImpresionAldo = async () => {
      try {
        const handle = await loadImpresionAldoHandle();
        if (!handle || cancelled) return;

        setImpresionAldoDirHandle(handle);

        const granted = await hasImpresionAldoPermission(handle, false);

        if (!cancelled) {
          setImpresionAldoDirStatus(
            granted
              ? `Configurada: ${handle.name}`
              : `Guardada: ${handle.name} (requiere autorización)`
          );
        }
      } catch (error) {
        console.error('No se pudo recuperar la carpeta de impresión ALDO:', error);
      }
    };

    cargarCarpetaImpresionAldo();

    return () => {
      cancelled = true;
    };
  }, []);

  const configurarCarpetaImpresionAldo = useCallback(async () => {
    if (!('showDirectoryPicker' in window)) {
      alert(
        'Este navegador no permite seleccionar una carpeta para escritura directa. Usa Chrome o Edge actualizado.'
      );
      return;
    }

    try {
      if (impresionAldoDirHandle) {
        const permisoActual = await hasImpresionAldoPermission(
          impresionAldoDirHandle,
          true
        );

        if (permisoActual) {
          setImpresionAldoDirStatus(
            `Configurada: ${impresionAldoDirHandle.name}`
          );
          alert(
            `Carpeta de impresión ALDO lista: ${impresionAldoDirHandle.name}`
          );
          return;
        }
      }

      const handle = await window.showDirectoryPicker({
        id: 'rav-impresion-aldo',
        mode: 'readwrite'
      });

      const permiso = await hasImpresionAldoPermission(handle, true);

      if (!permiso) {
        throw new Error(
          'No se concedió permiso de escritura sobre la carpeta seleccionada.'
        );
      }

      await saveImpresionAldoHandle(handle);

      setImpresionAldoDirHandle(handle);
      setImpresionAldoDirStatus(`Configurada: ${handle.name}`);

      alert(
        `Carpeta de impresión ALDO configurada: ${handle.name}`
      );
    } catch (error) {
      if (error?.name === 'AbortError') return;

      console.error('No se pudo configurar la carpeta de impresión ALDO:', error);

      alert(
        error?.message
        || 'No se pudo configurar la carpeta de impresión ALDO.'
      );
    }
  }, [impresionAldoDirHandle]);

  const obtenerOCrearCarpetaCorrida = useCallback(async () => {
    if (!impresionAldoDirHandle) {
      throw new Error(
        'Primero configura la carpeta raíz de impresión desde el botón "Configurar carpeta impresión ALDO".'
      );
    }

    const permiso = await hasImpresionAldoPermission(
      impresionAldoDirHandle,
      true
    );

    if (!permiso) {
      throw new Error(
        'No hay permiso de escritura sobre la carpeta de impresión configurada.'
      );
    }

    const now = new Date();
    const pad = (value) => String(value).padStart(2, '0');

    const fecha = [
      now.getFullYear(),
      pad(now.getMonth() + 1),
      pad(now.getDate())
    ].join('-');

    const corridaActual = corridaImpresionRef.current;

    const corridaCompleta = Boolean(
      corridaActual
      && corridaActual.generados?.has('Pedido')
      && corridaActual.generados?.has('Compra')
    );

    if (
      corridaActual
      && corridaActual.fecha === fecha
      && !corridaCompleta
    ) {
      return corridaActual;
    }

    const carpetaFecha = await impresionAldoDirHandle.getDirectoryHandle(
      fecha,
      { create: true }
    );

    const horaBase = [
      pad(now.getHours()),
      pad(now.getMinutes()),
      pad(now.getSeconds())
    ].join('-');

    let nombreCarpetaHora = horaBase;
    let consecutivo = 2;

    while (true) {
      try {
        await carpetaFecha.getDirectoryHandle(
          nombreCarpetaHora,
          { create: false }
        );
        nombreCarpetaHora = `${horaBase}_${consecutivo}`;
        consecutivo += 1;
      } catch (error) {
        if (error?.name === 'NotFoundError') break;
        throw error;
      }
    }

    const carpetaHora = await carpetaFecha.getDirectoryHandle(
      nombreCarpetaHora,
      { create: true }
    );

    const nuevaCorrida = {
      fecha,
      hora: nombreCarpetaHora,
      dirHandle: carpetaHora,
      generados: new Set()
    };

    corridaImpresionRef.current = nuevaCorrida;

    return nuevaCorrida;
  }, [impresionAldoDirHandle]);

  const fetchGlobalDuplicates = useCallback(() => {
    fetch(`${API_BASE_URL}/api/basedatos/obtener?full=1`)
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) {
          setBaseDuplicateKeys([]);
          return;
        }
        const counts = new Map();
        data.forEach(row => {
          const key = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
          if (!key) return;
          counts.set(key, (counts.get(key) || 0) + 1);
        });
        const duplicates = [];
        counts.forEach((count, key) => {
          if (count > 1) duplicates.push(key);
        });
        setBaseDuplicateKeys(duplicates);
      })
      .catch(err => {
        console.error('No se pudo calcular duplicados globales:', err);
        setBaseDuplicateKeys([]);
      });
  }, []);

  const columnDefs = useMemo(
    () => baseColumnDefs.map((column) => ({
      ...column,
      editable: column.field === 'PRECIO' ? (params => puedeEditarPrecioMultiasistencia && normalizarAseguradora(params?.data?.ASEGURADORA).includes('MULTIASISTENCIA')) : (CAPTURA_EDITABLE_FIELDS.has(column.field) || false),
    })),
    [puedeEditarPrecioMultiasistencia]
  );

  const baseColumnFields = useMemo(
    () => columnDefs
      .map(col => col.field)
      .filter((field) => Boolean(field)),
    [columnDefs]
  );

  const columnLabels = useMemo(() => {
    const labels = {};
    columnDefs.forEach(col => {
      if (col.field) {
        labels[col.field] = col.headerName || col.field;
      }
    });
    return labels;
  }, [columnDefs]);

  const defaultColumnVisibility = useMemo(() => {
    const defaults = {};
    baseColumnFields.forEach(field => {
      defaults[field] = true;
    });
    return defaults;
  }, [baseColumnFields]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: false,
      minWidth: 40,
      editable: false,
    }),
    []
  );

  const localeText = useMemo(
    () => ({
      noRowsToShow: loading ? 'Cargando datos…' : 'Sin pedidos con estado GENERAR',
    }),
    [loading]
  );

  useEffect(() => {
    if (columnVisibilityLoaded) return;
    const storedRaw = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (storedRaw) {
      try {
        const stored = JSON.parse(storedRaw);
        setColumnVisibility({ ...defaultColumnVisibility, ...stored });
        setColumnVisibilityLoaded(true);
        return;
      } catch (err) {
        console.warn('No se pudo leer la visibilidad guardada en captura:', err);
      }
    }
    setColumnVisibility(defaultColumnVisibility);
    setColumnVisibilityLoaded(true);
  }, [columnVisibilityLoaded, defaultColumnVisibility]);

  useEffect(() => {
    if (!columnVisibilityLoaded) return;
    localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility, columnVisibilityLoaded]);

  useEffect(() => {
    if (!columnVisibilityLoaded) return;
    setColumnVisibility(prev => {
      const next = { ...prev };
      let changed = false;
      baseColumnFields.forEach(field => {
        if (typeof next[field] === 'undefined') {
          next[field] = true;
          changed = true;
        }
      });
      Object.keys(next).forEach(field => {
        if (!baseColumnFields.includes(field)) {
          delete next[field];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [baseColumnFields, columnVisibilityLoaded]);

  const handleToggleColumnVisibility = useCallback((field) => {
    if (!columnVisibilityLoaded) return;
    setColumnVisibility(prev => {
      const currentlyVisible = prev[field] !== false;
      return { ...prev, [field]: !currentlyVisible };
    });
  }, [columnVisibilityLoaded]);

  const filteredColumnDefs = useMemo(() => {
    if (!columnVisibilityLoaded) return columnDefs;
    return columnDefs.filter(col => {
      if (!col.field) return true;
      return columnVisibility[col.field] !== false;
    });
  }, [columnDefs, columnVisibility, columnVisibilityLoaded]);

  const baseDuplicateSet = useMemo(() => {
    const set = new Set();
    baseDuplicateKeys.forEach(key => {
      if (key) set.add(key);
    });
    return set;
  }, [baseDuplicateKeys]);

  const capturaDuplicateSet = useMemo(() => {
    if (!Array.isArray(rowData) || rowData.length === 0) return new Set();
    const counts = new Map();
    rowData.forEach(row => {
      const key = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const duplicates = new Set();
    counts.forEach((count, key) => {
      if (count > 1) duplicates.add(key);
    });
    return duplicates;
  }, [rowData]);

  const combinedDuplicateSet = useMemo(() => {
    const union = new Set();
    baseDuplicateSet.forEach(key => union.add(key));
    capturaDuplicateSet.forEach(key => union.add(key));
    return union;
  }, [baseDuplicateSet, capturaDuplicateSet]);

  const applySearchFilter = useCallback((rows, text) => {
    if (!text) return rows;
    const query = text.toString().trim().toLowerCase();
    if (!query) return rows;
    const safeRows = Array.isArray(rows) ? rows : [];
    return safeRows.filter((row) => {
      if (!row || typeof row !== 'object') return false;
      return Object.values(row).some((value) => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(query);
      });
    });
  }, []);

  const filteredRowData = useMemo(() => {
    let result = Array.isArray(rowData) ? rowData : [];
    result = applySearchFilter(result, primarySearchText);
    result = applySearchFilter(result, secondarySearchText);
    return result;
  }, [applySearchFilter, primarySearchText, rowData, secondarySearchText]);

  const formattedHistory = useMemo(() => (
    lastUpdatedHistory
      .map((date) => formatTimestamp(date))
      .filter(Boolean)
  ), [lastUpdatedHistory]);

  const getRowClass = useCallback((params) => {
    const data = params?.data;
    if (!data) return '';
    const key = buildSiniestroItemKey(extractSiniestro(data), extractItem(data));
    if (key && combinedDuplicateSet.has(key)) {
      return 'row-texto-rojo';
    }
    return '';
  }, [combinedDuplicateSet]);

  const localidadesMap = useMemo(() => {
    const diccionario = new Map();
    if (Array.isArray(localidades)) {
      localidades.forEach((loc) => {
        const tallerValor = loc.taller ?? loc.TALLER ?? loc['NOMBRE COMERCIAL TALLER'] ?? '';
        const codigoValor = loc.CODIGO ?? loc.codigo ?? '';
        const nomenclaturaValor = loc.NOMENCLATURA ?? loc.nomenclatura ?? '';
        const key = normalizarTaller(tallerValor);
        if (key) {
          const codigoStr = codigoValor !== null && codigoValor !== undefined && codigoValor !== ''
            ? codigoValor.toString().trim()
            : '';
          const nomenclaturaStr = nomenclaturaValor !== null && nomenclaturaValor !== undefined && nomenclaturaValor !== ''
            ? nomenclaturaValor.toString().trim()
            : '';
          const current = diccionario.get(key) || [];
          current.push({ codigo: codigoStr, nomenclatura: nomenclaturaStr, taller: tallerValor.toString().trim() });
          diccionario.set(key, current);
        }
      });
    }
    return diccionario;
  }, [localidades]);

  const proveedoresCompraMap = useMemo(() => {
    const diccionario = new Map();

    if (Array.isArray(localidades)) {
      localidades.forEach((loc) => {
        const codigo = (loc.CODIGO ?? loc.codigo ?? '')
          .toString()
          .trim()
          .toUpperCase();

        const nombre = (
          loc['NOMBRE COMPAQ']
          ?? loc.nombreCompaq
          ?? loc.nombre_compaq
          ?? loc.taller
          ?? loc.TALLER
          ?? ''
        ).toString().trim();

        if (codigo && nombre && !diccionario.has(codigo)) {
          diccionario.set(codigo, nombre);
        }
      });
    }

    return diccionario;
  }, [localidades]);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
  const res = await fetch(`${API_BASE_URL}/api/basedatos/captura/generar`);
      if (!res.ok) {
        throw new Error(`Error ${res.status}`);
      }
      const payload = await res.json();
      const inboundTimestamp = typeof payload?.lastInboundAt === 'string' ? payload.lastInboundAt : null;
      const inboundDate = inboundTimestamp ? new Date(inboundTimestamp) : null;
      const parsedInboundDate = inboundDate && !Number.isNaN(inboundDate.getTime()) ? inboundDate : null;
      const payloadHistory = parseTimestampHistory(payload?.lastInboundHistory || payload?.history);
      const inboundHistory = payloadHistory.length
        ? payloadHistory
        : (parsedInboundDate ? [parsedInboundDate] : []);
      const rows = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.rows) ? payload.rows : null);
      if (!Array.isArray(rows)) {
        setRowData([]);
        previousRowIdsRef.current = new Set();
        if (inboundHistory.length) {
          setLastUpdatedHistory(inboundHistory);
        }
        return;
      }
      const sorted = [...rows].sort((a, b) => {
        const fechaA = parseFechaPedido(a.FECHA_PEDIDO);
        const fechaB = parseFechaPedido(b.FECHA_PEDIDO);
        return fechaB - fechaA;
      });
      const newRowIds = new Set();
      let detectedNewRows = sorted.length > 0 && previousRowIdsRef.current.size === 0;
      sorted.forEach((row) => {
        const identity = buildCapturaRowIdentity(row);
        if (!identity) return;
        newRowIds.add(identity);
        if (!previousRowIdsRef.current.has(identity)) {
          detectedNewRows = true;
        }
      });
      previousRowIdsRef.current = newRowIds;
      setRowData(sorted);
      if (gridRef.current) {
        gridRef.current.api.deselectAll();
      }
      setSelectedCount(0);
      setLastUpdatedHistory(prev => {
        if (inboundHistory.length) {
          return inboundHistory;
        }
        if (detectedNewRows) {
          const syntheticHistory = [new Date(), ...prev];
          return syntheticHistory.slice(0, CAPTURA_HISTORY_LIMIT);
        }
        return prev;
      });
      fetchGlobalDuplicates();
    } catch (err) {
      setRowData([]);
      setError('No se pudo cargar la información. Intenta nuevamente.');
      previousRowIdsRef.current = new Set();
    } finally {
      setLoading(false);
    }
  }, [fetchGlobalDuplicates]);

  useEffect(() => {
    if (puedeVer) {
      cargarDatos();
    }
  }, [cargarDatos, puedeVer]);

  useEffect(() => {
    fetchGlobalDuplicates();
    const handler = () => fetchGlobalDuplicates();
    window.addEventListener('refreshBaseDatos', handler);
    return () => window.removeEventListener('refreshBaseDatos', handler);
  }, [fetchGlobalDuplicates]);

  useEffect(() => {
    let cancelado = false;
    const cargarLocalidades = async () => {
      try {
  const res = await fetch(`${API_BASE_URL}/api/localidades`);
        if (!res.ok) {
          throw new Error(`Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelado) {
          setLocalidades(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelado) {
          setLocalidades([]);
        }
      }
    };
    cargarLocalidades();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!puedeVer) return undefined;
    const socket = socketIOClient(SOCKET_URL);
    const handleExcelUpdate = (payload) => {
      if (payload?.type === 'captura_inbound') {
        const incomingHistory = parseTimestampHistory(payload.history || [payload.lastInboundAt]);
        if (incomingHistory.length) {
          setLastUpdatedHistory(incomingHistory);
        } else if (typeof payload.lastInboundAt === 'string') {
          const inbound = new Date(payload.lastInboundAt);
          if (!Number.isNaN(inbound.getTime())) {
            setLastUpdatedHistory(prev => [inbound, ...prev].slice(0, CAPTURA_HISTORY_LIMIT));
          }
        }
        cargarDatos();
        return;
      }
      const targetId = payload?.id !== undefined ? Number(payload.id) : NaN;
      if (
        payload
        && payload.type === 'captura_cell_update'
        && Number.isInteger(targetId)
        && typeof payload.field === 'string'
      ) {
        setRowData((prev) => (
          Array.isArray(prev)
            ? prev.map((row) => (row.id === targetId ? { ...row, [payload.field]: payload.value } : row))
            : prev
        ));

        if (gridRef.current) {
          const rowNode = gridRef.current.api.getRowNode(String(targetId));
          if (rowNode) {
            gridRef.current.api.setFocusedCell(rowNode.rowIndex, payload.field);
          }
        }
        return;
      }
      cargarDatos();
    };

    socket.on('excel_data_updated', handleExcelUpdate);
    return () => {
      socket.off('excel_data_updated', handleExcelUpdate);
      socket.disconnect();
    };
  }, [cargarDatos, puedeVer]);

  const handleSelectionChanged = useCallback(() => {
    if (!gridRef.current) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    setSelectedCount(selectedRows.length);
  }, []);

  const handleCellValueChanged = useCallback((params) => {
    const { data, newValue, oldValue, colDef, node, api } = params || {};
    const field = colDef?.field;
    const esPrecioEditable = field === 'PRECIO' && puedeEditarPrecioMultiasistencia && normalizarAseguradora(data?.ASEGURADORA).includes('MULTIASISTENCIA');
    if (!field || (!CAPTURA_EDITABLE_FIELDS.has(field) && !esPrecioEditable)) return;
    const formattedNewValue = newValue == null ? '' : String(newValue);
    const formattedOldValue = oldValue == null ? '' : String(oldValue);
    if (formattedNewValue === formattedOldValue) return;

    setRowData((prev) => (
      Array.isArray(prev)
        ? prev.map((row) => (row.id === data?.id ? { ...row, [field]: formattedNewValue } : row))
        : prev
    ));

    if (api && node && Number.isInteger(node.rowIndex)) {
      window.requestAnimationFrame(() => {
        api.setFocusedCell(node.rowIndex, field);
      });
    }

    if (!data?.id) return;

    fetch(`${API_BASE_URL}/api/basedatos/captura/actualizar-celda`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: data.id, field, value: formattedNewValue, ...(field === 'PRECIO' ? { usuario: usuarioNombre } : {}) })
    }).then(async (res) => {
      if (field !== 'PRECIO') return;
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.ok) throw new Error(payload?.mensaje || 'No se pudo guardar PRECIO.');
    }).catch(() => {
      if (field === 'PRECIO') setRowData((prev) => Array.isArray(prev) ? prev.map((row) => row.id === data?.id ? { ...row, [field]: formattedOldValue } : row) : prev);
      setError('No se pudo guardar el cambio. Intenta nuevamente.');
    });
  }, [puedeEditarPrecioMultiasistencia, usuarioNombre]);

  const handleMarkAsGenerated = useCallback(async () => {
    if (!gridRef.current || marking || loading) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    const ids = selectedRows.map(row => row.id).filter(Boolean);
    if (!ids.length) {
      alert('Selecciona al menos un pedido para marcar como GENERADO.');
      return;
    }
    setMarking(true);
    setError('');
    try {
  const res = await fetch(`${API_BASE_URL}/api/basedatos/captura/marcar-generado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      if (!res.ok) {
        throw new Error(`Error ${res.status}`);
      }
      const data = await res.json();
      if (!data || data.ok === false) {
        throw new Error(data?.mensaje || 'No se pudo actualizar.');
      }
      const pedidosMultiAsignados = Number(data?.pedidosMultiAsignados || 0);
      if (pedidosMultiAsignados > 0) {
        const ejemplos = Array.isArray(data?.pedidosMultiEjemplos) && data.pedidosMultiEjemplos.length
          ? `\n${data.pedidosMultiEjemplos.join('\n')}`
          : '';
        alert(`Multiasistencia: se llenó PEDIDO con el folio de Compaq en ${pedidosMultiAsignados} registro(s).${ejemplos}`);
      }

      gridRef.current.api.deselectAll();
      setSelectedCount(0);
      await cargarDatos();
    } catch (err) {
      setError('No se pudo marcar como GENERADO. Intenta nuevamente.');
    } finally {
      setMarking(false);
    }
  }, [cargarDatos, loading, marking]);

  const prepareCompraRows = useCallback(() => {
    const rows = rowData.flatMap((row, index) => (
      expandRowByCodigo(row).map((expandedRow, splitIndex) => ({
        ...expandedRow,
        __originalIndex: index,
        __splitIndex: splitIndex,
      }))
    ));
    const pedidoCount = new Map();
    rows.forEach((row) => {
      const pedidoKey = (row.PEDIDO ?? '').toString().trim();
      if (!pedidoKey) return;
      pedidoCount.set(pedidoKey, (pedidoCount.get(pedidoKey) || 0) + 1);
    });
    rows.forEach((row) => {
      const pedidoKey = (row.PEDIDO ?? '').toString().trim();
      const codigo = resolveCodigoPorCosto(row);
      row.__agenteCompra = proveedoresCompraMap.get(codigo) || '';
      const count = pedidoKey ? pedidoCount.get(pedidoKey) || 0 : 0;
      const shouldAppend = Boolean(pedidoKey) && codigo === 'GDL005' && count > 1;
      const folioBase = buildFolioConSufijo(pedidoKey, shouldAppend);
      row.__folioAjustado = folioBase;
      row.__folioAjustadoFlag = shouldAppend;
      row.__pedidoClave = pedidoKey;
    });
    const grouped = new Map();
    rows.forEach((row) => {
      const key = row.__pedidoClave ?? '';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(row);
    });
    const ordered = [];
    grouped.forEach((groupRows) => {
      const sinAjuste = groupRows
        .filter(r => !r.__folioAjustadoFlag)
        .sort((a, b) => a.__originalIndex - b.__originalIndex);
      const conAjuste = groupRows
        .filter(r => r.__folioAjustadoFlag)
        .sort((a, b) => a.__originalIndex - b.__originalIndex);
      ordered.push(...sinAjuste, ...conAjuste);
    });
    return ordered;
  }, [rowData, proveedoresCompraMap]);

  const preparePedidoRows = useCallback(() => (
    rowData.map((row) => {
      if (!row) return row;
      const clone = { ...row };
      if (typeof clone.CODIGO === 'string' && clone.CODIGO.includes('/')) {
        const [primeroCodigo] = clone.CODIGO.split('/').map(part => part.trim()).filter(Boolean);
        clone.CODIGO = primeroCodigo ?? '';
      }
      if (typeof clone.PRECIO === 'string' && clone.PRECIO.includes('/')) {
        const [primeroPrecio] = clone.PRECIO.split('/').map(part => part.trim()).filter(Boolean);
        clone.PRECIO = primeroPrecio ?? '';
      }
      const tallerNombre = clone.NOMBRE_COMERCIAL_TALLER || '';
      const tallerKey = normalizarTaller(tallerNombre);
      if (tallerKey) {
        const localidadOptions = localidadesMap.get(tallerKey) || [];
        let localidadInfo = findLocalidadInfoForAseguradora(localidadOptions, clone.ASEGURADORA);

        if (esQualitasXnuupAseguradora(clone.ASEGURADORA) && !localidadInfo.codigo) {
          const todasLocalidades = Array.from(localidadesMap.values()).flat();
          localidadInfo = buscarLocalidadQualitasFlexible(todasLocalidades, tallerNombre);
        }

        clone.__cteProv = localidadInfo.codigo || '';
        clone.__serieNomenclatura = localidadInfo.nomenclatura || '';
      } else {
        clone.__cteProv = '';
        clone.__serieNomenclatura = '';
      }
      return clone;
    })
  ), [rowData, localidadesMap]);

  const buildTemplateData = useCallback((columns, customRows) => {
    const sourceRows = customRows || rowData;
    return sourceRows.map(row => (
      columns.map(col => {
        if (typeof col.valueGetter === 'function') {
          return col.valueGetter(row);
        }
        if (col.field) {
          const value = row[col.field];
          return value === null || value === undefined ? '' : value;
        }
        return '';
      })
    ));
  }, [rowData]);

  const registrarControlCompaq = useCallback(async (tipo, rows) => {
    const res = await fetch(`${API_BASE_URL}/api/basedatos/captura/control-compaq/agregar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tipo,
        rows
      })
    });

    let payload = null;
    try {
      payload = await res.json();
    } catch (_error) {
      payload = null;
    }

    if (!res.ok || payload?.ok === false) {
      throw new Error(
        payload?.mensaje || `No se pudo registrar ${tipo} en el control COMPAQ.`
      );
    }

    return payload;
  }, []);

  const generarPdfsAldo = useCallback(async (rows, corrida) => {
    const filasAldo = rows.filter(
      (row) => resolveCodigoPorCosto(row) === 'GDL002'
    );

    if (!filasAldo.length) {
      console.log('Plantilla Compra sin renglones GDL002/ALDO: no se generan PDFs.');
      return {
        generados: 0,
        sinOrden: 0
      };
    }

    const folioGetter = compraTemplateColumns.find(
      (col) => col.header === 'Folio'
    )?.valueGetter;

    const obtenerFolioCompra = (row) => (
      folioGetter
        ? String(folioGetter(row) ?? '').trim()
        : ''
    );

    const filasSinFolio = filasAldo.filter(
      (row) => !obtenerFolioCompra(row)
    );

    const filasConFolio = filasAldo.filter(
      (row) => obtenerFolioCompra(row)
    );

    if (filasSinFolio.length) {
      console.warn(
        `${filasSinFolio.length} renglón(es) GDL002 no tienen Folio en Columna C / Columna18 y no se incluirán en PDF.`,
        filasSinFolio
      );
    }

    const grupos = new Map();

    filasConFolio.forEach((row) => {
      const folioCompra = obtenerFolioCompra(row);

      if (!grupos.has(folioCompra)) {
        grupos.set(folioCompra, []);
      }

      grupos.get(folioCompra).push(row);
    });

    const carroGetter = compraTemplateColumns.find(
      (col) => col.header === 'Carro'
    )?.valueGetter;

    const now = new Date();
    const pad = (value) => String(value).padStart(2, '0');

    const fechaElaboracion = [
      now.getFullYear(),
      pad(now.getMonth() + 1),
      pad(now.getDate())
    ].join('-');

    const fechaHoraGeneracion = `${fechaElaboracion} ${[
      pad(now.getHours()),
      pad(now.getMinutes()),
      pad(now.getSeconds())
    ].join(':')}`;

    const siguienteGeneracion = async (baseNombre) => {
      let numero = 1;

      while (true) {
        const nombre = `${baseNombre}_GEN_${numero}.pdf`;

        try {
          await corrida.dirHandle.getFileHandle(
            nombre,
            { create: false }
          );
          numero += 1;
        } catch (error) {
          if (error?.name === 'NotFoundError') {
            return {
              numero,
              nombre
            };
          }

          throw error;
        }
      }
    };

    const dibujarEncabezadoTabla = (doc, y) => {
      const x = 10;

      const columnas = [
        { titulo: 'CANTIDAD', ancho: 18 },
        { titulo: 'CODIGO', ancho: 34 },
        { titulo: 'DESCRIPCION', ancho: 76 },
        { titulo: 'P. UNIT', ancho: 30 },
        { titulo: 'IMPORTE', ancho: 32 }
      ];

      let cursorX = x;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);

      columnas.forEach((col) => {
        doc.rect(cursorX, y, col.ancho, 8);
        doc.text(
          col.titulo,
          cursorX + (col.ancho / 2),
          y + 5,
          { align: 'center' }
        );
        cursorX += col.ancho;
      });

      return {
        y: y + 8,
        columnas
      };
    };

    let totalPdfs = 0;

    for (const [folioGrupo, groupRows] of grupos.entries()) {
      const firstRow = groupRows[0];

      const folio = folioGrupo;

      const siniestro = String(
        firstRow?.SINIESTRO ?? firstRow?.siniestro ?? ''
      ).trim();

      const vehiculo = carroGetter
        ? String(carroGetter(firstRow) ?? '').trim()
        : '';

      const taller = String(
        firstRow?.NOMBRE_COMERCIAL_TALLER ?? ''
      ).trim();

      const baseNombre = `ALDO_FOLIO_${normalizarNombreArchivoPdf(folio)}`;

      const {
        numero: numeroGeneracion,
        nombre: nombrePdf
      } = await siguienteGeneracion(baseNombre);

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const dibujarCabeceraDocumento = (continuacion = false) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text(
          continuacion
            ? 'OMAR ESQUEDA SILVA - CONTINUACION'
            : 'OMAR ESQUEDA SILVA',
          105,
          13,
          { align: 'center' }
        );

        doc.setFontSize(9);
        doc.text('PROVEEDOR:', 12, 26);
        doc.setFont('helvetica', 'normal');
        doc.text('GDL002 ALDO', 36, 26);

        doc.setFont('helvetica', 'bold');
        doc.text('COMPRA:', 118, 24);
        doc.setFont('helvetica', 'normal');
        doc.text(`RAV ${folio}`, 140, 24);

        doc.setFont('helvetica', 'bold');
        doc.text('F. ELABOR:', 118, 30);
        doc.setFont('helvetica', 'normal');
        doc.text(fechaElaboracion, 143, 30);

        doc.setFont('helvetica', 'bold');
        doc.text('O.C:', 118, 36);
        doc.setFont('helvetica', 'normal');
        doc.text(
          String(firstRow?.OC ?? '').trim() || '-',
          132,
          36
        );

        doc.setFont('helvetica', 'bold');
        doc.text('SINIESTRO:', 118, 42);
        doc.setFont('helvetica', 'normal');
        doc.text(
          doc.splitTextToSize(siniestro || '-', 58),
          145,
          42
        );

        doc.setFont('helvetica', 'bold');
        doc.text('VEHICULO:', 118, 48);
        doc.setFont('helvetica', 'normal');
        doc.text(
          doc.splitTextToSize(vehiculo || '-', 58),
          143,
          48
        );

        doc.setFont('helvetica', 'bold');
        doc.text('TALLER:', 118, 58);
        doc.setFont('helvetica', 'normal');
        doc.text(
          doc.splitTextToSize(taller || '-', 65),
          136,
          58
        );

        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `GENERADO: ${fechaHoraGeneracion}   GENERACION: ${numeroGeneracion}`,
          12,
          70
        );

        doc.setDrawColor(0);
      };

      dibujarCabeceraDocumento(false);

      let tabla = dibujarEncabezadoTabla(doc, 76);
      let y = tabla.y;

      let subtotal = 0;

      for (const row of groupRows) {
        const cantidad = 1;

        const codigo = String(
          row?.CODIGO ?? ''
        ).trim();

        const descripcion = String(
          row?.ITEM ?? ''
        ).trim();

        const costo = redondearMonedaPdf(
          parseMontoPdf(resolveCostoConDefault(row))
        );

        const importe = redondearMonedaPdf(
          costo * 1.16
        );

        subtotal = redondearMonedaPdf(
          subtotal + costo
        );

        const codigoLineas = doc.splitTextToSize(
          codigo || '-',
          30
        );

        const descripcionLineas = doc.splitTextToSize(
          descripcion || '-',
          72
        );

        const lineas = Math.max(
          1,
          codigoLineas.length,
          descripcionLineas.length
        );

        const altoFila = Math.max(
          8,
          (lineas * 4.2) + 3
        );

        if (y + altoFila > 246) {
          doc.addPage();
          dibujarCabeceraDocumento(true);
          tabla = dibujarEncabezadoTabla(doc, 76);
          y = tabla.y;
        }

        const anchos = [18, 34, 76, 30, 32];
        const xInicial = 10;

        let cursorX = xInicial;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);

        for (const ancho of anchos) {
          doc.rect(cursorX, y, ancho, altoFila);
          cursorX += ancho;
        }

        doc.text(
          String(cantidad),
          xInicial + 9,
          y + 5,
          { align: 'center' }
        );

        doc.text(
          codigoLineas,
          xInicial + 20,
          y + 5
        );

        doc.text(
          descripcionLineas,
          xInicial + 54,
          y + 5
        );

        doc.text(
          formatearMonedaPdf(costo),
          xInicial + 156,
          y + 5,
          { align: 'right' }
        );

        doc.text(
          formatearMonedaPdf(importe),
          xInicial + 188,
          y + 5,
          { align: 'right' }
        );

        y += altoFila;
      }

      const iva = redondearMonedaPdf(
        subtotal * 0.16
      );

      const total = redondearMonedaPdf(
        subtotal + iva
      );

      if (y > 235) {
        doc.addPage();
        dibujarCabeceraDocumento(true);
        y = 78;
      } else {
        y += 10;
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);

      doc.line(12, y + 22, 84, y + 22);
      doc.text(
        'FIRMA Y SELLO DEL TALLER',
        12,
        y + 27
      );

      const xEtiqueta = 135;
      const xMonto = 198;

      doc.setFont('helvetica', 'bold');

      doc.text(
        'SUBTOTAL:',
        xEtiqueta,
        y + 4
      );

      doc.text(
        formatearMonedaPdf(subtotal),
        xMonto,
        y + 4,
        { align: 'right' }
      );

      doc.text(
        'I.V.A.:',
        xEtiqueta,
        y + 11
      );

      doc.text(
        formatearMonedaPdf(iva),
        xMonto,
        y + 11,
        { align: 'right' }
      );

      doc.text(
        'TOTAL:',
        xEtiqueta,
        y + 18
      );

      doc.text(
        formatearMonedaPdf(total),
        xMonto,
        y + 18,
        { align: 'right' }
      );

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);

      const letras = doc.splitTextToSize(
        importeALetrasPdf(total),
        110
      );

      doc.text(
        letras,
        12,
        y + 38
      );

      const pdfArrayBuffer = doc.output('arraybuffer');

      const fileHandle = await corrida.dirHandle.getFileHandle(
        nombrePdf,
        { create: true }
      );

      const writable = await fileHandle.createWritable();

      await writable.write(pdfArrayBuffer);
      await writable.close();

      totalPdfs += 1;

      console.log(
        `PDF ALDO guardado en ${corrida.fecha}/${corrida.hora}/${nombrePdf}`
      );
    }

    if (filasSinFolio.length) {
      alert(
        `Se generaron ${totalPdfs} PDF(s) ALDO, pero ${filasSinFolio.length} renglón(es) GDL002 no tenían Folio en la columna C y no se incluyeron en PDF.`
      );
    }

    return {
      generados: totalPdfs,
      sinFolio: filasSinFolio.length
    };
  }, []);

  const downloadTemplate = useCallback(async (columns, fileLabel, sourceRows) => {
    if (!sourceRows.length) {
      alert('No hay registros pendientes para generar la plantilla.');
      return;
    }

    let corrida;

    try {
      corrida = await obtenerOCrearCarpetaCorrida();
    } catch (error) {
      console.error('No se pudo preparar la carpeta de la corrida:', error);
      alert(
        error?.message
        || 'No se pudo preparar la carpeta de la corrida.'
      );
      return;
    }

    let rows;
    if (columns === compraTemplateColumns) {
      rows = prepareCompraRows();
    } else if (columns === pedidoTemplateColumns) {
      rows = preparePedidoRows();
    } else {
      rows = sourceRows;
    }
    const data = buildTemplateData(columns, rows);

    try {
      await registrarControlCompaq(fileLabel, data);
    } catch (error) {
      console.error('No se pudo registrar la plantilla en el control COMPAQ:', error);
      alert(
        error?.message
        || 'No se pudo registrar la plantilla en el control COMPAQ. No se descargó el archivo.'
      );
      return;
    }

    try {
      const contenido = await generarXlsxDesdeMoldeCompaq(data);

      const nombreArchivo = `Plantilla_${fileLabel}.xlsx`;

      const fileHandle = await corrida.dirHandle.getFileHandle(
        nombreArchivo,
        { create: true }
      );

      const writable = await fileHandle.createWritable();
      await writable.write(contenido);
      await writable.close();

      let resultadoPdfs = null;

      if (fileLabel === 'Compra') {
        resultadoPdfs = await generarPdfsAldo(rows, corrida);
      }

      corrida.generados.add(fileLabel);

      console.log(
        `Plantilla ${fileLabel} guardada en ${corrida.fecha}/${corrida.hora}/${nombreArchivo}`
      );

      const detallePdfs = fileLabel === 'Compra'
        ? `\nPDFs ALDO generados: ${resultadoPdfs?.generados ?? 0}`
        : '';

      alert(
        `Plantilla ${fileLabel} guardada correctamente.\n\n`
        + `Carpeta: ${corrida.fecha} / ${corrida.hora}`
        + detallePdfs
      );
    } catch (error) {
      console.error(`No se pudo guardar Plantilla ${fileLabel}:`, error);
      alert(
        `No se pudo guardar Plantilla ${fileLabel} en la carpeta de la corrida.`
      );
    }
  }, [
    buildTemplateData,
    generarPdfsAldo,
    obtenerOCrearCarpetaCorrida,
    prepareCompraRows,
    preparePedidoRows,
    registrarControlCompaq
  ]);

  const downloadCompaqTemplate = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/basedatos/captura/control-compaq/descargar`
      );

      if (!res.ok) {
        let payload = null;
        try {
          payload = await res.json();
        } catch (_error) {
          payload = null;
        }

        throw new Error(
          payload?.mensaje || 'No se pudo descargar el control COMPAQ del día.'
        );
      }

      const blob = await res.blob();

      const disposition = res.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(
        /filename\*?=(?:UTF-8''|")?([^";]+)/
      );

      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim())
        : 'CONTROL_COMPAQ_DIARIO.xlsx';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('No se pudo descargar el control COMPAQ:', error);
      alert(
        error?.message
        || 'No se pudo descargar el control COMPAQ del día.'
      );
    }
  }, []);

  const deleteControlCompaq = useCallback(async () => {
    const confirmar = window.confirm(
      '¿Seguro que quieres borrar TODO el control COMPAQ acumulado?\n\nEsta acción no borra Plantilla Pedido ni Plantilla Compra.'
    );

    if (!confirmar) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/basedatos/captura/control-compaq/borrar`,
        { method: 'DELETE' }
      );

      let payload = null;
      try {
        payload = await res.json();
      } catch (_error) {
        payload = null;
      }

      if (!res.ok || payload?.ok === false) {
        throw new Error(
          payload?.mensaje || 'No se pudo borrar el control COMPAQ.'
        );
      }

      alert(payload?.mensaje || 'Control COMPAQ borrado correctamente.');
      setShowCompaqActions(false);
    } catch (error) {
      console.error('No se pudo borrar el control COMPAQ:', error);
      alert(
        error?.message
        || 'No se pudo borrar el control COMPAQ.'
      );
    }
  }, []);

  if (!puedeVer) {
    return (
      <div className="captura-denied">
        <div className="captura-denied__card">
          <h2>Acceso restringido</h2>
          <p>No cuentas con permisos para ingresar al módulo de captura.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="captura-page">
      <section className="captura-card captura-table-card">
        <header className="captura-card__header captura-table-header">
          <div>
            <h3 className="captura-card__title captura-card__title--small">Pedidos pendientes por capturar</h3>
            <p className="captura-card__subtitle">
              Filtrados automáticamente mostrando únicamente registros donde COMPAQ es GENERAR.
            </p>
          </div>
          <div className="captura-table-actions">
            <span className="captura-selection">Seleccionados: {selectedCount}</span>
            <button
              type="button"
              className="captura-template"
              onClick={() => setShowColumnList(prev => !prev)}
              disabled={loading}
            >
              {showColumnList ? 'Ocultar columnas' : 'Seleccionar columnas'}
            </button>
            <button
              type="button"
              className="captura-refresh"
              onClick={cargarDatos}
              disabled={loading}
            >
              {loading ? 'Cargando…' : 'Actualizar'}
            </button>
            <button
              type="button"
              className="captura-template captura-template--pedido"
              onClick={() => downloadTemplate(pedidoTemplateColumns, 'Pedido', rowData)}
              disabled={loading || marking || !rowData.length}
            >
              Plantilla Pedido
            </button>
            <button
              type="button"
              className="captura-template"
              onClick={configurarCarpetaImpresionAldo}
              disabled={loading || marking}
              title={impresionAldoDirStatus}
            >
              {impresionAldoDirHandle
                ? `Carpeta ALDO: ${impresionAldoDirHandle.name}`
                : 'Configurar carpeta impresión ALDO'}
            </button>
            <button
              type="button"
              className="captura-template captura-template--compra"
              onClick={() => downloadTemplate(compraTemplateColumns, 'Compra', rowData)}
              disabled={loading || marking || !rowData.length}
            >
              Plantilla Compra
            </button>
            <button
              type="button"
              className="captura-template"
              onClick={() => setShowCompaqActions(prev => !prev)}
              disabled={loading || marking}
            >
              Plantilla COMPAQ
            </button>
            {showCompaqActions && (
              <>
                <button
                  type="button"
                  className="captura-template"
                  onClick={downloadCompaqTemplate}
                  disabled={loading || marking}
                >
                  Descargar control
                </button>
                <button
                  type="button"
                  className="captura-template"
                  onClick={deleteControlCompaq}
                  disabled={loading || marking}
                >
                  Borrar control
                </button>
              </>
            )}
            <button
              type="button"
              className="captura-mark"
              onClick={handleMarkAsGenerated}
              disabled={loading || marking || selectedCount === 0}
            >
              GENERADO
            </button>
          </div>
        </header>
        <div className="captura-filters">
          <div className="captura-filters__group">
            <input
              type="text"
              placeholder="Buscar en la tabla…"
              value={primarySearchText}
              onChange={(event) => setPrimarySearchText(event.target.value)}
            />
            {primarySearchText && (
              <button type="button" onClick={() => setPrimarySearchText('')}>
                Limpiar
              </button>
            )}
          </div>
          <div className="captura-filters__group">
            <input
              type="text"
              placeholder="Aplicar segundo filtro…"
              value={secondarySearchText}
              onChange={(event) => setSecondarySearchText(event.target.value)}
            />
            {secondarySearchText && (
              <button type="button" onClick={() => setSecondarySearchText('')}>
                Limpiar
              </button>
            )}
          </div>
        </div>
        {showColumnList && (
          <div
            style={{
              margin: '12px 0',
              padding: 12,
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              background: '#f9fafb',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10
            }}
          >
            {baseColumnFields.map(field => (
              <label
                key={field}
                style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 180, fontSize: 13 }}
              >
                <input
                  type="checkbox"
                  checked={columnVisibility[field] !== false}
                  onChange={() => handleToggleColumnVisibility(field)}
                />
                <span>{columnLabels[field] || field}</span>
              </label>
            ))}
          </div>
        )}
        {error && <div className="captura-alert">{error}</div>}
        <div className="captura-grid-wrapper">
          <div className="ag-theme-alpine captura-grid-table">
            <AgGridReact
              ref={gridRef}
              columnDefs={filteredColumnDefs}
              rowData={filteredRowData}
              defaultColDef={defaultColDef}
              getRowClass={getRowClass}
              rowSelection="multiple"
              domLayout="normal"
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              suppressScrollOnNewData={true}
              enterMovesDownAfterEdit={false}
              enterMovesDown={false}
              enableCellTextSelection={true}
              enableBrowserTooltips={true}
              headerHeight={32}
              rowHeight={28}
              localeText={localeText}
              onSelectionChanged={handleSelectionChanged}
              onCellValueChanged={handleCellValueChanged}
              getRowId={(params) => (params?.data?.id ? String(params.data.id) : undefined)}
            />
          </div>
        </div>
      </section>
      <section className="captura-card captura-card--secondary">
        <header className="captura-card__header">
          <div>
            <h3 className="captura-card__title captura-card__title--small">Resumen del día</h3>
            <p className="captura-card__subtitle">
              Mantén el equipo informado. Solicita evidencias faltantes y comparte actualizaciones en tiempo real.
            </p>
          </div>
        </header>
        <div className="captura-summary">
          <div className="captura-summary__item">
            <span className="captura-summary__label">Pedidos pendientes</span>
            <span className="captura-summary__value">{loading ? '…' : rowData.length}</span>
          </div>
          <div className="captura-summary__item">
            <span className="captura-summary__label">Evidencias por revisar</span>
            <span className="captura-summary__value">—</span>
          </div>
          <div className="captura-summary__item captura-summary__item--history">
            <span className="captura-summary__label">Última hora de codificación</span>
            {formattedHistory.length ? (
              <div className="captura-summary__history">
                {formattedHistory.map((value, index) => (
                  <div key={`${value}-${index}`} className="captura-summary__history-row">
                    <span className="captura-summary__history-dot" aria-hidden="true" />
                    <span className="captura-summary__history-value">{value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="captura-summary__value captura-summary__value--muted">Sin datos</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CapturaPage;
